import type { CSSProperties } from 'react';
import {
  COVER_COLOURS,
  type CardCover,
  type CardCoverPut,
  type CoverColour,
  type CoverSize,
  type CoverTextTreatment,
} from '@molt/shared';
import { labelColourDisplayName, labelSwatch } from '@/components/palette';
import { BOARD_BACKGROUND_PHOTOS, boardBackgroundPresetLabel } from './board-backgrounds';

/**
 * FE-80.1 `card-cover-tail`. Declared here rather than imported from
 * `interface-language.tsx` for the reason `BoardBackgroundTranslate` already
 * settled one module over: a `lib/` leaf states the shape of what it needs and
 * stays importable by a test that hands it a doctored table.
 */
export type CardCoverTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

export interface CoverVisual {
  readonly style: CSSProperties | undefined;
  readonly inkClassName: string;
  readonly label: string;
  readonly isImageLike: boolean;
}

export interface CardCoverPhotoOption {
  readonly photoRef: string;
  readonly label: string;
  readonly attribution: string | null;
  readonly style: CSSProperties;
}

/**
 * FE-80.1 `card-cover-tail` — `t` is the REQUIRED FIRST parameter, with no
 * default, and that is the whole mechanism.
 *
 * ⚠ THIS HELPER FEEDS TWO SURFACES. `CardCoverSection.tsx:79` (the card back,
 * where `label` is both an `aria-label` and visible text) and
 * `CardTile.tsx:933` (the board front, where it is the `aria-label` on both
 * cover render sites). A translator DEFAULTED here would let one caller fall
 * through and render English on the other surface with nothing red anywhere —
 * the same failure `card-title.ts`'s docblock records. Making it required makes
 * `tsc` enumerate every call site instead.
 *
 * ⚠ THE COLOUR SLOT IS FILLED BY `labelColourDisplayName(t, …)`, NEVER BY
 * `labelColourName()`. See the catalogue comment beside
 * `cardBack.cover.visual.colour`, and `board-frame.routing.test.ts:194-205`
 * for `BoardMapView` making the identical migration.
 *
 * `label` values are byte-identical to what this returned before routing, so
 * `e2e/FR-CARD-025.spec.ts`'s four `getByLabel('Green cover' | 'Blue cover' |
 * 'Attachment cover')` assertions are unaffected.
 */
export function coverVisual(
  t: CardCoverTranslate,
  cover: CardCover | null,
): CoverVisual | null {
  if (cover === null) return null;

  if (cover.source === 'colour') {
    const swatch = labelSwatch(cover.colour as CoverColour);
    return {
      style:
        swatch === null
          ? { backgroundColor: 'var(--color-board)' }
          : { backgroundColor: swatch.fill },
      inkClassName:
        cover.textTreatment === 'light' ? 'text-board-ink-light' : 'text-board-ink-dark',
      label: t('cardBack.cover.visual.colour', {
        colour: labelColourDisplayName(t, cover.colour as CoverColour),
      }),
      isImageLike: false,
    };
  }

  if (cover.source === 'photo_library') {
    const photo = BOARD_BACKGROUND_PHOTOS.find(
      (entry) => entry.photoRef === cover.photoRef,
    );
    return {
      style:
        photo === undefined
          ? {
              backgroundColor: 'var(--color-board)',
              backgroundImage:
                'linear-gradient(135deg, var(--color-panel), var(--color-board))',
            }
          : photo.style,
      inkClassName:
        cover.textTreatment === 'light' ? 'text-board-ink-light' : 'text-board-ink-dark',
      // ⚠ BOTH HALVES ROUTE. `photo.name` is the DATUM and `photo.labelKey` is
      // the reader's half — routing only the `??` fallback would ship a routed
      // fallback beside an unrouted primary, which is precisely the defect
      // `board-backgrounds.ts:200-212` documents.
      label:
        photo === undefined
          ? t('cardBack.cover.visual.photo')
          : boardBackgroundPresetLabel(t, photo),
      isImageLike: true,
    };
  }

  return {
    style: {
      backgroundColor: 'var(--color-board)',
      backgroundImage:
        'repeating-linear-gradient(135deg, var(--color-panel) 0 8px, var(--color-surface-raised) 8px 16px)',
    },
    inkClassName:
      cover.textTreatment === 'light' ? 'text-board-ink-light' : 'text-board-ink-dark',
    label: t('cardBack.cover.visual.attachment'),
    isImageLike: true,
  };
}

/**
 * FE-80.1 `card-cover-tail`. Routed for the same reason `coverVisual`'s colour
 * arm is: these ten labels render as the swatch buttons' `aria-label` and text
 * at `CardCoverSection.tsx:322/:332`, one popover away from the cover label
 * this slice routed. `LabelPicker.tsx:242/:323` is the analogous picker already
 * calling `labelColourDisplayName(t, colour)`; leaving these behind would print
 * `Green` beside a translated `{colour} cover` inside one dialog.
 *
 * ⚠ `primitives.labelColour.*` is byte-identical to `labelColourName()`'s
 * output for all ten cover colours, so nothing a test reads changes.
 */
export function coverColourOptions(t: CardCoverTranslate): readonly {
  value: CoverColour;
  label: string;
  style: CSSProperties;
  inkClassName: string;
}[] {
  return COVER_COLOURS.map((value) => {
    const swatch = labelSwatch(value);
    return {
      value,
      label: labelColourDisplayName(t, value),
      style: swatch === null ? {} : { backgroundColor: swatch.fill },
      inkClassName:
        swatch?.ink === 'light' ? 'text-board-ink-light' : 'text-board-ink-dark',
    };
  });
}

/**
 * FE-80.1 `card-cover-tail`. `label` resolves through
 * `boardBackgroundPresetLabel(t, entry)`, the `name`/`labelKey` split
 * `board-backgrounds.ts` already settled; the picker renders it at
 * `CardCoverSection.tsx:356`.
 *
 * ⚠ `attribution` STAYS THE WIRE VALUE and is deliberately NOT routed. It is
 * what `CardCoverSection` sends as the cover's `photoAttribution`, landing in
 * `cards.cover_photo_attribution`; `board-backgrounds.ts:22-33` states it and
 * `e2e/FR-EXP-022.spec.ts` pins the exact bytes on the board export. Nothing in
 * this module renders it. Its reader-facing twin is `boardBackgroundPhotoAttribution`.
 */
export function coverPhotoOptions(
  t: CardCoverTranslate,
): readonly CardCoverPhotoOption[] {
  return BOARD_BACKGROUND_PHOTOS.map((entry) => ({
    photoRef: entry.photoRef,
    label: boardBackgroundPresetLabel(t, entry),
    attribution: entry.attribution,
    style: entry.style,
  }));
}

export function colourCoverPut(input: {
  colour: CoverColour;
  size: CoverSize;
  textTreatment: CoverTextTreatment;
}): CardCoverPut {
  return { source: 'colour', ...input };
}

export function photoLibraryCoverPut(input: {
  photoRef: string;
  photoAttribution: string | null;
  size: CoverSize;
  textTreatment: CoverTextTreatment;
}): CardCoverPut {
  return { source: 'photo_library', ...input };
}

export function attachmentCoverPut(input: {
  attachmentId: string;
  size: CoverSize;
  textTreatment: CoverTextTreatment;
}): CardCoverPut {
  return { source: 'attachment', ...input };
}
